// Libraries
import { orderBy } from 'lodash';

// Types
import GenericContact, { GenericContactKeys } from '@/types/generic-contact';
import { SortingField, SortOrder } from '@/types/data';
import Locale from '@/types/locale';
import Portfolio from '@/types/portfolio';
import { FactsetContact, FactsetHousehold } from '@/types/factset-household';
import { ReducedPortfolioFDC3 } from '@/types/portfolio-fdc3';
import { Household } from '@/types/households';

// Utils
import { getFullTextSearchRegex } from '@/utils/regex';
import { transformFDC3BasePortfolio } from '../portfolios';

export const postMessageFilterContacts = (contacts: GenericContact[], query: string): GenericContact[] => {
  const defaultFieldsToFilterOn: GenericContactKeys[] = ['fname', 'lname', 'uid'];
  const matchedContactsIds: Set<string> = new Set<string>();
  const regX: RegExp = getFullTextSearchRegex(query);

  const matchedContacts = contacts.filter((contact: GenericContact) => {
    if (matchedContactsIds.has(contact._id!)) {
      return false;
    }

    for (const field of defaultFieldsToFilterOn) {
      if (contact[field] != null && regX.test(contact[field] as string)) {
        matchedContactsIds.add(contact._id!);
        return true;
      }
    }

    return false;
  });

  return matchedContacts;
};

export const postMessageFilterContactPortfolios = (locale: Locale, portfolios: Portfolio[], query: string): Portfolio[] => {
  const regX: RegExp = getFullTextSearchRegex(query);
  return portfolios.filter((portfolio: Portfolio) => regX.test(portfolio.name[locale] as string));
};

export const postMessageSortContacts = (contacts: GenericContact[], sort?: SortingField[]): GenericContact[] => {
  const defaultSortingField: SortingField = { id: 'lname', order: SortOrder.Asc };
  const sortingField: SortingField = sort?.length ? sort[0] : defaultSortingField;

  return orderBy(contacts, [sortingField.id], [sortingField.order]);
};

export const postMessageSortContactPortfolios = (locale: Locale, portfolios: Portfolio[], sort?: SortingField[]): Portfolio[] => {
  const defaultSortingField: SortingField = { id: 'name', order: SortOrder.Asc };
  const sortingField: SortingField = sort?.length ? sort[0] : defaultSortingField;
  const localizedSortingField = `${sortingField.id}.${locale}`;

  return orderBy(portfolios, [localizedSortingField], [sortingField.order]);
};

export const formatContact = (factsetContact: FactsetContact, factsetHousehold?: FactsetHousehold): GenericContact => {
  const name = factsetContact.name.trim().split(' ');
  const capintelContact: GenericContact = {
    _id: factsetContact.id.FDS_ID,
    fname: name[0],
    lname: name.slice(1).join(' '),
  };

  if (factsetHousehold) {
    capintelContact.householdId = factsetHousehold.id;
  }

  return capintelContact;
};

export const formatHouseholdData = (factsetHousehold: FactsetHousehold) => {
  const formattedContacts: GenericContact[] = [];
  const formattedPortfolios: Portfolio[] = [];

  factsetHousehold.contacts.forEach((factsetContact: FactsetContact) => {
    // Formatting Contact Info
    const capintelContact: GenericContact = formatContact(factsetContact, factsetHousehold);
    formattedContacts.push(capintelContact);

    // Formatting Portfolio Info
    formattedPortfolios.push(...formatContactPortfolios(factsetContact, capintelContact));
  });

  return { formattedContacts, formattedPortfolios };
};

export const formatHousehold = (factsetHousehold: FactsetHousehold): Household => {
  const { formattedContacts } = formatHouseholdData(factsetHousehold);
  const capintelHousehold: Household = {
    id: factsetHousehold.id,
    name: factsetHousehold.name,
    contacts: formattedContacts,
  };

  return capintelHousehold;
};

export const formatContactPortfolios = (factsetContact: FactsetContact, capintelContact: GenericContact) => {
  const formattedPortfolios: Portfolio[] = [];

  factsetContact.portfolios.forEach((portfolio: ReducedPortfolioFDC3) => {
    const transformedPortfolio: Portfolio = transformFDC3BasePortfolio(portfolio);
    transformedPortfolio.contact = capintelContact;
    formattedPortfolios.push(transformedPortfolio);
  });

  return formattedPortfolios;
};
